import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import { withRouter } from 'react-router-dom';
import _get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import queryString from 'query-string';

import { subDays } from 'date-fns';
import { addWarantyToCar } from '../services/warranty.service';
import { getWarrantiesForCar, getWarrantyOptionsForCar } from '../../dictionary/services/dictionary.service';
import { getCar } from '../../car/services/car.service';
import WarrantyModel from '../models/warranty.model';

import { Datepicker, CustomSelect } from '../../common/components';
import FormDetailsItem from '../../common/components/formDetailItem.component';

const initialState = {
  isLoading: true,
  car: null,
  warrantyOptions: [],
  formData: {
    dateFrom: new Date(),
    warrantyType: null,
    options: [],
  },
  sum: 0,
};

const styles = {
  multiValueRemove: (base, state) => (state.data.isFixed ? { ...base, display: 'none' } : base),
};

const getWarrantyOptionLabel = (option) => `${option.name} ${option.duration} msc - ${option.price} PLN`;

const getWarrantyAdditionalOptionLabel = (option) => `${option.name} - ${option.price} PLN`;

const getOptionValue = (option) => option.id;

const noOptionsMessage = () => 'Brak gwarancji dla podanego wieku lub przebiegu samochodu';

class Warranty extends PureComponent {
  state = cloneDeep(initialState);

  async componentDidMount() {
    this.queryValues = queryString.parse(this.props.location.search);

    const carInfo = await getCar(this.queryValues.car);
    const { data: warranties } = await getWarrantiesForCar({ car: this.queryValues.car });

    this.setState({
      isLoading: false,
      warranties: warranties.map((w) => ({ ...w, price: w.groups[0].price, actualPrice: w.groups[0].actualPrice })),
      carInfo,
    });
  }

  warrantyTypeChange = async (_, warrantyType) => {
    const { data: warrantyOptions } = await getWarrantyOptionsForCar({ car: this.queryValues.car, warrantyType: warrantyType.id });
    const checkedOptions = warrantyOptions.filter((v) => v.isFixed);

    this.setState({
      formData: {
        ...this.state.formData,
        warrantyType,
        options: checkedOptions,
      },
      warrantyOptions,
    });
  }

  warrantyOptionChange = (_, options, option) => {
    if (option && option.removedValue && option.removedValue.isFixed) {
      return;
    }

    this.setState({
      formData: {
        ...this.state.formData,
        options: options || [],
      },
    });
  }

  onInputChange = (name, value) => {
    this.setState({
      formData: {
        ...this.state.formData,
        [name]: value,
      },
    });
  }

  onFormSave = async () => {
    const { history } = this.props;
    const { formData } = this.state;

    const newFormData = {
      ...formData,
      car: this.queryValues.car * 1,
      price: this.calculatePrice(),
    };

    const warrantyModel = new WarrantyModel(newFormData);
    const errors = warrantyModel.validate();

    if (!errors) {
      try {
        await addWarantyToCar(warrantyModel.getData());

        history.replace(`/car-details/${this.queryValues.car}`);
      } catch (err) {
        console.log(err);
      }
    }

    this.setState({ errors });
  }

  calculatePrice = () => {
    const { formData } = this.state;
    const options = _get(formData, 'options', []);
    let sum = _get(formData, 'warrantyType.price', 0) * 1;

    if (options && options.length) {
      options.forEach((item) => sum += _get(item, 'price', 0) * 1);
    }

    return sum;
  }

  render() {
    const { isAdmin } = this.props;
    const {
      warranties,
      warrantyOptions,
      carInfo,
      formData,
      isLoading,
      errors,
    } = this.state;


    if (isLoading) {
      return null;
    }

    return (
      <>
        <div className="content">
          <div className="card">
            <div className="card-header">
              Szczegóły samochodu
            </div>
            <div className="card-body">
              <FormDetailsItem label="Firma" value={`${_get(carInfo, 'company.name')} (NIP ${_get(carInfo, 'company.vatId')})`} />
              <FormDetailsItem label="Marka" value={_get(carInfo, 'manufacturer.name')} />
              <FormDetailsItem label="Model" value={_get(carInfo, 'model.name')} />
              <FormDetailsItem label="Rok produkcji" value={_get(carInfo, 'year')} />
              <FormDetailsItem label="Rodzaj paliwa" value={_get(carInfo, 'fuelType.name')} />
              <FormDetailsItem label="Pojemność" value={`${_get(carInfo, 'engine')} cm3`} />
              <FormDetailsItem label="Moc" value={`${_get(carInfo, 'power')} kW | ${(_get(carInfo, 'power') * 1.36).toFixed(0)} KM`} />
              <FormDetailsItem label="Przebieg" value={_get(carInfo, 'mileage')} />
            </div>
          </div>
        </div>

        <div className="content">
          <div className="card">
            <div className="card-header">
              Dodawanie nowej gwarancji
            </div>
            <div className="card-body">
              {/* <FormDetailsItem label="Samochód" value={`${carInfo.manufacturer.name} ${carInfo.model.name} (VIN ${carInfo.vin})`} /> */}

              <div className="form-horizontal">
                <Datepicker
                  selected={formData.dateFrom}
                  id="dateFrom"
                  label="Data rozpoczęcia gwarancji"
                  minDate={!isAdmin ? subDays(new Date(), 3) : null}
                  maxDate={new Date()}
                  onChange={this.onInputChange}
                  errors={errors}
                  inline
                />
                <div className="row form-group">
                  <div className="col col-md-3">
                    <label className="form-control-label">Rodzaj gwarancji</label>
                  </div>
                  <div className="col-12 col-md-9">
                    <CustomSelect
                      data={{ id: 'dictWarrantyType' }}
                      value={formData.warrantyType}
                      onChange={this.warrantyTypeChange}
                      options={warranties}
                      getOptionLabel={getWarrantyOptionLabel}
                      getOptionValue={getOptionValue}
                      errors={errors}
                      noOptionsMessage={noOptionsMessage}
                    />
                  </div>
                </div>
                <div className="row form-group">
                  <div className="col col-md-3">
                    <label className="form-control-label">Opcje dodatkowe</label>
                  </div>
                  <div className="col-12 col-md-9">
                    <CustomSelect
                      data={{ id: 'warrantyOptions' }}
                      styles={styles}
                      value={formData.options}
                      options={warrantyOptions}
                      onChange={this.warrantyOptionChange}
                      getOptionLabel={getWarrantyAdditionalOptionLabel}
                      getOptionValue={getOptionValue}
                      isClearable={formData.options.some((v) => !v.isFixed)}
                      errors={errors}
                      isMulti
                    />
                  </div>
                </div>
                <div className="row form-group">
                  <div className="col col-md-3">
                    <label className="form-control-label">SUMA</label>
                  </div>
                  <div className="col-12 col-md-9 font-weight-bold">
                    {this.calculatePrice()}
                    {' '}
                    PLN
                  </div>
                </div>
              </div>
            </div>
            <div className="mr-4">
              <div className="form-group float-right">
                <button type="button" onClick={this.onFormSave} className="btn btn-primary btn-sm">
                  <i className="fa fa-dot-circle-o" />
                  Zapisz
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
}

Warranty.defaultProps = {

};

Warranty.propTypes = {
  history: ReactRouterPropTypes.history.isRequired,
  location: ReactRouterPropTypes.location.isRequired,
  isAdmin: PropTypes.bool.isRequired,
};

export default withRouter(Warranty);
